import axios from 'axios'
import type { AxiosResponse } from 'axios'
import { useUserStore } from '@/stores/user'
import { useRouter, useRoute } from 'vue-router'

// API 基础配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const FILE_UPLOAD_URL = 'https://api.style.iqismart.com/api'
// const FILE_UPLOAD_URL = 'https://avalon.jiangfengoa.com/api'
const FILE_UPLOAD_TOKEN = 'eyJhbGciOiJIUzI1NiIsInppcCI6IkRFRiJ9.eJyrVirNTFGyMtRRSi5KTSxJDcnMTQVyzQ3NjE0MjCxNzQzNdJSK8nNSi5WsopUSU3Iz85RiawG1IRAO.BK1eJ4RfTsGj5-JoYe4Aukcm39JjZoZGXeFTUUNp_Ec'
const router = useRouter()
// 在文件开头添加类型定义
interface ApiAxiosResponse<T = any> {
  code: number
  msg?: string
  message?: string
  data: T
}

// 添加响应类型定义
interface ApiResponse<T = any> {
  code: number
  msg: string
  data: T
}

// 创建请求实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 360000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    'versionCode': '135'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    const token = localStorage.getItem('token')
    // console.log('userStore.token',token)
    // console.log('localstorage token',localStorage.getItem('token'))
    if (token) {
      config.headers.token = token
    }else{
      console.log('token不存在,跳转到登陆页面')
      // 跳转到登陆页面
      // router.push({ name: 'login' })
    }
    config.headers.versionCode = '135'
    
    // 打印请求信息
    console.log('请求配置:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data
    })
    
    return config
  },
  (error) => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('响应数据:', response.data)
    return response.data as ApiAxiosResponse
  },
  (error) => {
    if (error.message === 'Network Error') {
      error.message = '网络异常'
    }
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      error.message = '网络异常'
    }
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 产品详情接口返回类型
export interface ProductItemDetail {
  id: number
  productOrderId: number
  no: string
  batch: string
  name: string
  count: string
  draws: string
  submitReviewStatus: number
  financeReviewStatus: number
  groupReviewStatus: number
  submitReviewTime?: number
  financeReviewTime?: number
  groupReviewTime?: number
  order?: {
    id: number
    so: string
    customer: string
    customerName: string
    customerNo: string
  }
}
// 合同相关接口
export const contractApi = {
  /**
   * 获取单位列表
   */
  async getUnitList() {
    return api.get('/jf/unit/list')
  },
  /**
   * 获取预置产品简称
   */
  async getProductNameList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post('/jf/contract/getPresetProductShortNameList', params)
  },
  /**
   * 获取预置公司列表
   */
  async getCompanyList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post('/jf/contract/getPresetCompanyList', params)
  },
  /**
   * 获取项目名称列表
   */
  async getProjectNameList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post('/jf/preset/project/search', params)
  },
  /**
   * 获取预置物料列表
   */
  async getProjectTypeList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post('/jf/contract/getPresetMaterialList', params)
  },
  /**
   * 获取负责人数据
   */
  async getOwnerList() {
    return api.get('/jf/contract/getOwnerList')
  },
  /**
   * 获取合同列表
   * @param params 查询参数
   */
  async getList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post('/jf/contract/salesmanList', params)
  },
  /**
   * 增加编辑合同接口
   * @param params 新增或编辑合同参数
   */
  async addOrUpdate(params: {
    id?: number
    companyName: string
    customerShortName: string
    projectName: string
    projectType: string
    region: string
    description: string
    customerDocUrl: string
    submit: number
    productList: [{
      id: number
      productName: string
      quantity: number
    }]
  }) {
    return api.post('/jf/contract/salesmanCreateOrUpdate', params)
  },
  /**
   * 获取合同详情
   * @param id 合同ID
   */
  async getDetail(id: number) {
    return api.post(`/jf/contract/contractDetail`, { id })
  },
  /**
   * 获取商品详情
   * @param id 商品ID
   */
  async getProductDetail(id: number) {
    return api.post(`/jf/contract/productDetail`, { id })
  },
  /**
   * 获取商品详情带物料
   */
  async getProductDetailWithMaterial(id: number) {
    return api.post(`/jf/contract/productDetail`, { id,needMaterial: 1 })
  },
  /**
   * 转设计&估价
   */
  async toPricingAndDesignByProduct(params:any) {
    return api.post(`/jf/contract/toPricingAndDesignByProduct`,params)
  },
  // 产品转设计
  async toDesignByProduct(params:any) {
    return api.post(`/jf/contract/toDesignByProduct`, params)
  },
  // 产品转生产
  async toProductionByProduct(params:any) {
    return api.post(`/jf/contract/toProduction`, params)
  },
  /**
   * 转物控
   */
  async toWuliaoStatus(params:any) {
    return api.post(`/jf/contract/toWuliaoStatus`, params)
  },
  /**
   * 估价员待领取合同列表
   */
  async getPriceList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post(`/jf/contract/pricingPendingList`, params)
  },
  /**
   * 估价员领取合同
   * @param id 商品ID
   */
  async getPricingClaim(id: number) {
    return api.post(`/jf/contract/pricingClaim`, { id })
  },
  /**
   * 估价员合同列表
   */
  async getPricingList(params: { page: number; pageSize: number; keyword?: string, statusPricing?: number }) {
    return api.post(`/jf/contract/pricingList`, params)
  },
  /**
   * 更新商品物料清单
   */
  async updateProductMaterial(params: { id: number; materialList: any[] }) {
    return api.post(`/jf/contract/updateProductMaterial`, params)
  },
  /**
   * 完成估价
   */
  async completePricing(params: { id: number }) {
    return api.post(`/jf/contract/completePricing`, params)
  },
  // 取消完成估价
  async cancelCompletePricing(params: { id: number }) {
    return api.post(`/jf/contract/cancelCompletePricing`, params)
  },
  // 获取物料列表数据
  async getProductMaterialList(params: { id: number; page?: number; pageSize?: number }) {
    return api.post(`/jf/contract/productMaterialList`, params)
  },
  /**
   * 提交估价
   */
  async estimateSubmit(params: { id: number }) {
    return api.post(`/jf/contract/pricingSubmit`, params)
  },
  /**
   * 提交审核
   */
  async submitAudit(params: { id: number }) {
    return api.post(`/jf/contract/salesmanSubmitAudit`, params)
  },
  /**
   * 集团审核
   */
  async toGroup(params: { id: number }) {
    return api.post(`/jf/contract/toGroup`, params)
  },
  /**
   * 财务审核
   */
  async toFinance(params: { id: number }) {
    return api.post(`/jf/contract/toFinance`, params)
  },
  /**
   * 获取财务审核列表
   */
  async getAuditList(params: { page: number; pageSize: number; keyword?: string, statusFinance?: number }) {
    return api.post(`/jf/contract/financeWaitAuditList  `, params)
  },
  /**
   * 获取集团审核列表
   */
  async getGroupAuditList(params: { page: number; pageSize: number; keyword?: string, statusGroup?: number }) {
    return api.post(`/jf/contract/groupWaitAuditList`, params)
  },
  /**
   * 财务审核
   */
  async financeAudit(params: { id: number, statusFinance: number }) {
    return api.post(`/jf/contract/financeAudit`, params)
  },
  /**
   * 集团审核
   */
  async groupAudit(params: { id: number, statusGroup: number }) {
    return api.post(`/jf/contract/groupAudit`, params)
  },
  // 转物控操作
  async toWuliao(params: { id: number }) {
    return api.post(`/jf/contract/toWuliao`, params)
  },
  // 获取物控列表
  // async wuliaoWaitClaimList(params: { page: number; pageSize: number; keyword?: string; statusWuliao?: number }) {
  //   return api.post(`/jf/contract/wuliaoWaitClaimList`, params)
  // },
  async wuliaoSummaryList(params: any) {
    return api.post(`/jf/contract/wuliaoSummaryList`, params)
  },
  // 获取我的物控列表
  // async wuliaoWaitedClaimList(params: { page: number; pageSize: number; keyword?: string; statusWuliao?: number }) {
  //   return api.post(`/jf/contract/wuliaoWaitedClaimList`, params)
  // },
  async wuliaoSummaryMyList(params: { page: number; pageSize: number; keyword?: string; statusWuliao?: number }) {
    return api.post(`/jf/contract/wuliaoSummaryMyList`, params)
  },
  // 物控合同列表
  async wuliaoContractList(params: any) {
    return api.post(`/jf/contract/wuliaoContractList`, params)
  },
  // 获取物控清单
  async getWuliaoList(params: { id: number }) {
    return api.post(`/jf/contract/getWuliaoList`, params)
  },
  // 更新物料
  async updateWuliao(params: { id: number, list: any[] }) {
    return api.post(`/jf/contract/updateWuliao`, params)
  },
  // 删除 移除物料
  deleteProductMaterial(params: { productId: number, materialId: number }) {
    return api.post(`/jf/contract/deleteProductMaterial`, params)
  },
  // 更新价格信息
  async updateProductMaterialSummary(params: { id: number }) {
    return api.post(`/jf/contract/updateProductMaterialSummary`, params)
  },
  // 获取物控详情
  async getWuliaoDetail(params: { id: number }) {
    return api.post(`/jf/contract/getWuliaoDetail`, params)
  },
  // 物控确认
  // async wuliaoClaim(params: { id: number }) {
  //   return api.post(`/jf/contract/wuliaoClaim`, params)
  // },
  async wuliaoSummaryClaim(params: { id: number }) {
    return api.post(`/jf/contract/wuliaoSummaryClaim`, params)
  },
  // 物控员添加物控清单
  // async addWuliaoItem(params: {  
  //   id: number; itemList: any[]
  // }) {
  //   return api.post(`/jf/contract/addWuliaoItem`, params)
  // },
  // 业务员确认物控清单明细
  async salesmanConfirmWuliaoItem(params: {
    id: number; salesmanReviewStatus: number ,salesmanReason: string
  }) {
    return api.post(`/jf/contract/salesmanConfirmWuliaoItem`, params)
  },
  // 物控主管确认物控清单明细
  async wuliaoManagerConfirmWuliaoItem(params: {
    id: number; wuliaoReviewStatus: number
  }) {
    return api.post(`/jf/contract/wuliaoManagerConfirmWuliaoItem`, params)
  },
  // 转设计
  async toDesign(params: { id: number }) {
    return api.post(`/jf/contract/toDesign`, params)
  },
  // 获取设计列表
  async designWaitClaimList(params: any) {
    return api.post(`/jf/contract/designWaitClaimList`, params)
  },
  // 获取设计列表
  async designList(params: { page: number; pageSize: number; keyword?: string }) {
    return api.post(`/jf/contract/designList`, params)
  },
  // 专案领取
  async designClaimContract(params: any) {
    return api.post(`/jf/contract/designClaimContract`, params)
  },
  // 按产品获取我的设计列表
  async designWaitedClaimList(params: { page: number; pageSize: number; keyword?: string, statusDesign?: number }) {
    return api.post(`/jf/contract/designWaitedClaimList`, params)
  },
  // 按项目获取我的设计列表
  async getMyDesignByContract(params: { page: number; pageSize: number; keyword?: string, statusDesign?: number }) {
    return api.post(`/jf/contract/getMyDesignByContract`, params)
  },
  // 领取设计
  async designClaim(params: { id: number }) {
    return api.post(`/jf/contract/designClaim`, params)
  },
  // 分配设计
  async designAssign(params: { id: number, uid: number , designDifficulty: number}) {
    return api.post(`/jf/contract/designAssign`, params)
  },
  // 分配人员
  async designAssignableUsers() {
    return api.get(`/jf/contract/designAssignableUsers`)
  },
  // 产品分配
  async productAssign(params: { id: number, uid: number, type: string }) {
    return api.post(`/jf/contract/productAssign`, params)
  },
  // 设置设计难度
  async setDesignDifficulty(params: { id: number, designDifficulty: number }) {
    return api.post(`/jf/contract/setDesignDifficulty`, params)
  },
  // 分配负责人
  async assignOwner(params: { id: number, uid: number }) {
    return api.post(`/jf/contract/assignOwner`, params)
  },
  // 设计员提交
  async designSubmit(params: { id: number }) {
    return api.post(`/jf/contract/designSubmit`, params)
  },
  // 设计上传文档
  async uploadFileArchives(params: any) {
    return api.post(`/jf/fileArchives/upload`, params)
  },
  // 获取设计留档文件
  async getAllFileArchivesByProductOrderItemId(params: any) {
    return api.get(`/jf/fileArchives/getAllFileArchivesByProductOrderItemId?productOrderItemId=${params.productOrderItemId}`)
  },
  // 获取合同图纸
  async getContractDraws(params: { id: number }) {
    return api.post(`/jf/contract/getContractDraws`, params)
  },
  // 获取汇率
  async getExchangeRate(params: { currency: string }) {
    return api.get(`/jf/contract/getExchangeRate?currency=${params.currency}`)
  },
  // 获取产品下的区域信息
  async getProductArea(params: { productOrderId: number }) {
    return api.get(`/jf/contractProductArea/getAreaByProductOrderId?productOrderId=${params.productOrderId}`)
  },
  // 新增或修改产品的区域
  async saveOrUpdateArea(params: any) {
    return api.post(`/jf/contractProductArea/saveOrUpdateArea`, params)
  },
  // 获取楼栋 层数 区域/房型
  async getAreaDataTips(params: any) {
    return api.get(`/jf/contractProductArea/getAreaDataTips?contractId=${params.contractId}`)
  },
  // 修改产品排序
  async updateProductSort(params: any) {
    return api.post(`/jf/contract/updateProductSort`, params)
  },
  // 估价修改产品排序
  async updateProductPricingSort(params: any) {
    return api.post(`/jf/contract/updateProductPricingSort`, params)
  },
  // 保存物料
  async addWuliaoItem(params: any) {
    return api.post(`/jf/contract/addWuliaoItem`, params)
  },
  // 会议纪要上传
  async uploadMeetingMinutes(params: any) {
    return api.post(`/jf/contract/meetingMinutes/upload`, params)
  },
  // 获取会议纪要列表
  async getMeetingMinutesList(params: any) {
    return api.get(`/jf/contract/meetingMinutes/list?productOrderId=${params.contractId}&dept=${params.dept}`)
  },
  // 更新会议纪要
  async updateMeetingMinutes(params: any) {
    return api.post(`/jf/contract/meetingMinutes/update`, params)
  },
  // 获取问题
  async getProblemList(params: any) {
    return api.get(`/jf/contract/meetingMinutes/questionsList`)
  },
  // 更新问题
  async updateProblem(params: any) {
    return api.post(`/jf/contract/meetingMinutes/update`, params)
  },
  // 上传客户文档
  async updateCustomerDocUrl (params: any) {
    return api.post(`/jf/contract/updateCustomerDocUrl `, params)
  },
  // 上传物料书
  async uploadMaterialFile(params: any) {
    return api.post(`/jf/materialFile/upload`, params)
  },
  // 删除物料书
  async deleteMaterialFile(params: any) {
    return api.post(`/jf/materialFile/delete`, params)
  },
  // 获取物料书
  async getMaterialFile(params: any) {
    return api.get(`/jf/materialFile/list?productOrderId=${params.id}`)
  },
  // 获取物料书内容
  async getMaterialFileBySyscode(params: any) {
    return api.get(`/jf/materialFile/listBySyscode?syscode=${params.syscode}&productOrderId=${params.productOrderId}`)
  },
  // 导出excel
  async exportProductMaterial(params: any) {
    return api.get(`/excel/exportProductMaterial?contractId=${params.contractId}`
    //   , {
    //   responseType: 'blob'
    // }
    )
  },
  // 查看导出状态
  async exportStatus(params: any) {
    return api.get(`/excel/exportStatus?contractId=${params.contractId}`)
  },
  // 导入excel
  async importProduct(params: any) {
    return api.post(`/jf/contract/importProduct`, params)
  },
  // excel sheet获取
  async getExcelSheetList(params: any) {
    return api.post(`/jf/contract/getExcelSheetList`, params)
  },  
  // 图片导入产品
  async extractProductImage(params: any) {
    return api.post(`/jf/ai/extractProductImage`, params)
  },
  // 查询产品导入是否成功
  async extractProductImageStatus(params: any) {
    return api.post(`/jf/ai/extractProductImageStatus`, params)
  },
  // 图片导入物料
  async extractMaterialImage(params: any) {
    return api.post(`/jf/ai/extractMaterialImage`, params)
  },
  // 查询物料导入是否成功
  async extractMaterialImageStatus(params: any) {
    return api.post(`/jf/ai/extractMaterialImageStatus`, params)
  },
  // 查询导入是否成功
  async findImportProductStatus(params: any) {
    return api.post(`/jf/contract/findImportProductStatus`, params)
  },
  // 更新系数配置
  async updateContractConfig(params: any) {
    return api.post(`/jf/contract/updateContractConfig`, params)
  },
  // 获取系数配置
  async getContractConfig(id: number) {
    return api.get(`/jf/contract/getContractConfig?id=${id}`)
  },
  // 获取合同下产品列表
  async getProductList(params: any) {
    return api.post(`/jf/contract/productList`, params)
  },
  // 获取合同下区域汇总
  async getAreaSummary(params: any) {
    return api.post(`/jf/contractProductArea/getAreaSummary`, params)
  },
  // 区域下上传平面图
  async uploadAreaImage(params: any) {
    return api.post(`/jf/contractProductArea/uploadAreaImage`, params)
  },
  // 删除区域下的平面图
  async deleteAreaImage(params: any) {
    return api.post(`/jf/contractProductArea/deleteAreaImage`, params)
  },
  // 获取区域区域唯一标识
  async getAreaImageKeyList(params: any) {
    return api.get(`/jf/contractProductArea/getAreaImageKeyList?contractId=${params.contractId}`)
  },
  // 区域下的产品
  async getAreaProductList(params: any) {
    return api.post(`/jf/contractProductArea/getAreaProductList`, params)
  },
  // 获取项目维度下的生管列表
  async getProductionList(params: any) {
    return api.post(`/jf/contract/productionList`, params)
  },
  // 获取产品维度下的生管列表
  async getContractProductList(params: any) {
    return api.post(`/jf/contract/getContractProductList`, params)
  },
  // 按项目维度下的生产列表
  async getFactoryContractList(params: any) {
    return api.post(`/jf/contract/factory/contractList`, params)
  },
  // 按产品维度下的生产列表
  async getFactoryProductList(params: any) {
    return api.post(`/jf/contract/factory/productList`, params)
  },
  // 转生产的产品作废处理
  async toProductionInvalid(params: any) {
    return api.post(`/jf/contract/cancelProduction`, params)
  },
  // 导入生产BOM
  async importProductionBom(params: any) {
    return api.post(`/jf/production-bom/import`, params)
  },
  // 获取bom的sheet列表
  async getProductionBomSheetList(params: any) {
    return api.post(`/jf/production-bom/getSheetList`, params)
  },
  // 查询生产BOM导入状态
  async getProductionBomImportStatus(params: any) {
    return api.get(`/jf/production-bom/import/status?taskId=${params.taskId}`)
  },
  // 获取物料清单
  async loadMaterialBomItems(params: any) {
    return api.post(`/jf/production-bom/items`, params)
  },
  // 获取物料汇总
  async loadMaterialSummary(params: any) {
    return api.post(`/jf/production-bom/summary`, params)
  },
  // 分配工厂工艺
  async assignFactory(params: any) {
    return api.post(`/jf/productOrderItem/assignFactory`, params)
  },
  // 删除产品
  async deleteProduct(params: any) {
    return api.post(`/jf/contract/deleteProduct`, params)
  },
  // 增加修改产品
  async addOrUpdateProduct(params: any) {
    return api.post(`/jf/contract/addOrUpdateProduct`, params)
  },
  // 增加物料库
  async addPresetMaterial(params: any) {
    return api.post(`/jf/contract/addPresetMaterial`, params)
  },
  // 删除物料
  async deletePresetMaterial(params: any) {
    return api.post(`/jf/contract/deletePresetMaterial`, params)
  },
  // 导入物料库
  async importPresetMaterialExcel(params: any) {
    return api.post(`/jf/contract/importPresetMaterialExcel`, params)
  },
  // 获取物料
  async getWuliaoListPage(params: any) {
    return api.post(`/jf/contract/getWuliaoListPage`, params)
  },
  // 增加修改物料
  async addOrUpdateWuliao(params: any) {
    return api.post(`/jf/contract/addOrUpdateWuliao`, params)
  },
  // 删除物料
  async deleteWuliao(params: any) {
    return api.post(`/jf/contract/deleteWuliao`, params)
  },
  // 获取物料列表
  async getContractMaterialPrice(params: any) {
    return api.post(`/jf/contract/getContractMaterialPrice`, params)
  },
  // 获取集团合同信息
  async getGroupOverview(params: any) {
    return api.post(`/jf/contract/report/overview/get`, params)
  },
  // 获取集团合同列表
  async getGroupOverviewList(params: any) {
    return api.post(`/jf/contract/report/overview/list`, params)
  },
  // 财务记录列表
  async getFinanceRecordList(params: any) {
    return api.post(`/jf/contract/finance/list`, params)
  },
  // 删除财务记录
  async deleteFinanceRecord(params: any) {
    return api.post(`/jf/contract/finance/delete`, params)
  },
  // 新增财务记录
  async addFinanceRecord(params: any) {
    return api.post(`/jf/contract/finance/add`, params)
  },
  // 更新财务记录
  async updateFinanceRecord(params: any) {
    return api.post(`/jf/contract/finance/update`, params)
  },
  // 上传合同
  async uploadContractPDF(params: any) {
    return api.post(`/jf/contractPDF/uploadContractPDF`, params)
  },
  // 获取合同
  async getContractInfo(params: any) {
    return api.get(`/jf/contractPDF/getContractInfoByContractId?contractId=${params.contractId}`)
  },
  // 上传凭证接口
  async uploadVoucher(params: any) {
    return api.post(`/jf/contract/finance/uploadVoucher`, params)
  },
  // 获取凭证接口
  async getVoucherInfo(params: any) {
    return api.get(`/jf/contract/finance/getVoucherInfo?contractId=${params.contractId}`)
  },
  // 上传发票接口
  async uploadInvoice(params: any) {
    return api.post(`/jf/contractFinanceInvoiceRecord/uploadInvoice`, params)
  },
  // 获取发票接口
  async getInvoiceInfo(params: any) {
    return api.get(`/jf/contractFinanceInvoiceRecord/getInvoiceInfo?contractId=${params.contractId}`)
  },
  // 设计搜索图片
  async searchImage(params: any) {
    return api.post(`/productOrder/searchImage`, params)
  },
  // 以图搜图
  async searchImageByImage(params: any) {
    return api.post(`/jf/aliyun/image/search`, params)
  },
  // 获取修改日志
  async getProductModifyLogs(params: any) {
    return api.get(`/jf/contract/getProductModifyLogs?productOrderItemId=${params.productOrderItemId}&page=${params.page}&pageSize=${params.pageSize}&type=${params.type}&hideQuote=${params.hideQuote}`)
  },
  // 更新用户查看日志时间
  async updateUserViewProductLogTime(params: any) {
    return api.get(`/jf/userViewProductLog/updateTime?productOrderId=${params.productOrderId}`)
  },
  // 产品日志是否显示红点接口
  async getProductModifyLogsShowRed(params: any) {
    return api.get(`/jf/userViewProductLog/showRed?productOrderId=${params.productOrderId}`)
  },
  async getProductModifyLogs2(params: any) {
    return api.get(`/jf/contract/getProductModifyLogs?page=${params.page}&pageSize=${params.pageSize}&type=${params.type}&wuliaoId=${params.wuliaoId}`)
  },
  // 搜索产品
  async searchProduct(params: any) {
    return api.post(`/jf/contract/searchProduct`, params)
  },
  // 复制产品物料
  async copyProductMaterial(params: any) {
    return api.post(`/jf/contract/copyProductMaterial`, params)
  },
  // 相似图片产品物料推荐
  async getSimilarImage(params: any) {
    return api.post(`/jf/aliyun/image/similar`, params)
  },
  // 删除合同
  async deleteContract(params: any) {
    return api.post(`/jf/contract/deleteContract`, params)
  },
  // 恢复合同
  async restoreContract(params: any) {
    return api.post(`/jf/contract/restoreContract`, params)
  },
  // 设计部门人员总览
  async getDesignStatistics(params: any) {
    return api.post(`/jf/design/statistics/query`, params)
  },
  // 获取所有部门
  async getDepartmentsList() {
    return api.get(`/jf/announcement/manage/departments`)
  },
  // 料单汇总
  async mergeProductionBomItems(params: any) {
    return api.post(`/jf/production-bom/items/merge`, params)
  },
}

// 费用报销相关接口
export const expenseClaimApi = {
  // 获取费用报销列表
  async getFinanceList(params: any) {
    return api.post(`/jf/expenseClaim/financeList`, params)
  },
  // 审核报销
  async completeReimbursement(params: any) {
    return api.post(`/jf/expenseClaim/completeReimbursement`, params)
  }
}
// 工时相关接口
export const timeApi = {
  // 获取工厂项
  async getFactories() {
    return api.get(`/jf/attendance/statistics/factories`)
  },
  // 获取部门项
  async getDepartments(factory: string) {
    return api.get(`/jf/attendance/statistics/departments?factory=${factory}`)
  },
  // 获取组项
  async getGroups(factory: string, department: string) {
    return api.get(`/jf/attendance/statistics/groups?factory=${factory}&department=${department}`)
  },
  // 获取工时统计
  async getStatisticsList(params: any) {
    return api.post(`/jf/attendance/statistics/query`, params)
  },
  // 导出工时
  async exportTable(params: any) {
    return api.post(`/jf/attendance/statistics/export`, params)
  }
}

// 订单相关接口
export const orderApi = {
  /**
   * 获取订单详情
   * @param productOrderId 订单ID
   * @param module 模块类型，默认为0
   */
  async getDetail(productOrderId: number, module = 0) {
    // 打印调用者信息
    const stack = new Error().stack
    const caller = stack?.split('\n')[2]?.trim()
    console.log('调用者信息:', caller)
    console.log('获取订单详情:', { productOrderId, module })
    return api.get(`/productOrder/orderDetail`, {
      params: {
        productOrderId,
        module
      }
    })
  },

  /**
   * 获取产品详情
   * @param productOrderItemId 产品ID
   */
  async getItemDetail(productOrderItemId: number): Promise<ApiResponse<ProductItemDetail>> {
    try {
      console.log('获取产品详情, productOrderItemId:', productOrderItemId)
      const response = await api.post(`/productOrder/orderItemDetail`, {
        productOrderItemId
      })
      
      return {
        code: response.code || 0,
        msg: response.msg || '获取产品失败',
        data: {
          ...response.data,
          submitReviewStatus: response.data?.submitReviewStatus || 0,
          financeReviewStatus: response.data?.financeReviewStatus || 0,
          groupReviewStatus: response.data?.groupReviewStatus || 0,
          submitReviewTime: response.data?.submitReviewTime,
          financeReviewTime: response.data?.financeReviewTime,
          groupReviewTime: response.data?.groupReviewTime
        }
      }
    } catch (error: any) {
      console.error('获取产品详情失败:', error)
      throw new Error(error.message || '获取产品详情失败，请重试')
    }
  },

  /**
   * 获取订单图纸列表
   * @param productOrderId 订单ID
   */
  async getDraws(productOrderId: number) {
    return api.get(`/productOrder/draws/${productOrderId}`)
  },

  /**
   * 更新图纸信息
   * @param params 更新参数
   */
  async updateDraw(params: {
    productOrderId: number
    type: number
    img: string
    operate: number
  }) {
    return api.post('/productOrder/updateDraw', params)
  },

  /**
   * 删除图纸
   * @param params 删除参数
   */
  async deleteImage(params: {
    productOrderId: number
    imageUrl: string
    module: number
  }) {
    return api.post('/productOrder/deleteImage', params)
  },

  /**
   * 上传文件到文件服务器
   * @param file 文件对象
   * @param onProgress 进度回调
   */
  async uploadFile(file: File, onProgress?: (progress: number) => void) {
    const formData = new FormData()
    formData.append('file', file)

    try {
      console.log('开始上传文件:', file.name)
      const response = await api.post('/productOrder/uploadFile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      })

      console.log('文件上传响应:', response)
      if (!response.data?.url) {
        throw new Error(`文件 ${file.name} 上传失败: 未获取到URL`)
      }

      return response.data
    } catch (error: any) {
      console.error('文件上传失败:', error)
      throw new Error(error.response?.data?.msg || `文件 ${file.name} 上传失败，请重试`)
    }
  },

  /**
   * 替换产品图纸
   * @param params 替换参数
   */
  async replaceDraw(params: {
    productOrderItemId: number
    type: number
    img: string
    target: string
  }) {
    return api.post('/productOrder/updateDraw', {
      ...params,
      operate: 1
    })
  },
}

// 认证相关接口
export const authApi = {
  /**
   * 短信登录
   * @param params 登录参数
   */
  async login(params: { mobilePhone: string; code: string }) {
    const res = await api.post('/api/user/smsLogin', params)
    console.log('登录响应:', res)  // 添加日志
    return res
  },
  /**
   * 检查token
   */
  async checkToken() {
    return api.get('/api/user/checkToken')
  },
  /**
   * 退出登录
   */
  async logout() {
    return api.post('/api/user/logout')
  },

  /**
   * 发送短信验证码
   * @param mobile 手机号
   */
  async sendSmsCode(mobile: string) {
    return api.post('/api/user/sendSmsCode', {
      mobilePhone: mobile
    })
  }
}

// 产品项关接口
export const productApi = {
  /**
   * 获取产品列表
   * @param params 查询参数
   */
  async getList(params: { page: number; size: number; keyword?: string }) {
    return api.get('/product/list', { params })
  },

  /**
   * 获取产品详情
   * @param id 产品ID
   */
  async getDetail(id: number) {
    return api.get(`/product/detail/${id}`)
  },

  /**
   * 获取产品图纸列表
   * @param productId 产品ID
   */
  async getDrawList(productId: number) {
    return api.get(`/product/draws/${productId}`)
  }
}

// 导出便捷函数
export const getProductList = productApi.getList
export const getProductDetail = productApi.getDetail
export const getDrawList = productApi.getDrawList

// 搜索相关接口
export const searchApi = {
  /**
   * 搜索订单
   * @param params 搜索参数
   */
  async searchOrders(params: {
    keyword: string
    page: number
    pageSize: number
    type: number
  }) {
    return api.post('/productOrder/search', params)
  }
}

// 文件上传相关接口
export const uploadApi = {
  /**
   * 上传单个文件
   * @param file 文件对象
   * @param onProgress 上传进度回调
   */
  async uploadFile(file: File, onProgress?: (progress: number) => void) {
    const formData = new FormData()
    formData.append('file', file)

    try {
      console.log('开始上传文件:', file.name)
      const response = await axios.post(
        `${FILE_UPLOAD_URL}/file/upload/form?type=jf&token=${FILE_UPLOAD_TOKEN}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress(progress)
            }
          },
        }
      )

      console.log('文件上传响应:', response)
      if (!response.data?.data?.url) {
        throw new Error(`文件 ${file.name} 上传失败: 未获取到URL`)
      }

      return {
        url: response.data.data.url
      }
    } catch (error: any) {
      console.error('文件上传失败:', error)
      throw new Error(error.response?.data?.msg || `文件 ${file.name} 上传失败，请重试`)
    }
  },

  /**
   * 批量上传文件
   * @param files 文件列表
   * @param onProgress 整体上传进度回调
   * @param onFileProgress 单个文件上传进度回调
   */
  async uploadFiles(
    files: File[],
    onProgress?: (progress: number) => void,
    onFileProgress?: (fileIndex: number, progress: number) => void
  ) {
    const results: string[] = []
    const totalFiles = files.length

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // 上传单个文件
        const result = await this.uploadFile(file, (progress) => {
          if (onFileProgress) {
            onFileProgress(i, progress)
          }
          if (onProgress) {
            // 计算总体进度
            const totalProgress = Math.round(((i * 100) + progress) / totalFiles)
            onProgress(totalProgress)
          }
        })
        
        if (result.url) {
          results.push(result.url)
        } else {
          throw new Error(`文件 ${file.name} 上传失败: 未获取到URL`)
        }
      }
      
      return results
    } catch (error: any) {
      console.error('批量上传文件失败:', error)
      throw new Error(error.message || '文件上传失败，请重试')
    }
  },

  /**
   * 根据URL获取文件列表
   * @param urls 文件URL列表
   */
  async getFilesByUrls(urls: string[]) {
    try {
      console.log('获取文件列表:', urls) 
      const response = await axios.post(
        `${FILE_UPLOAD_URL}/file/getFiles?token=${FILE_UPLOAD_TOKEN}`, 
        {
          urls: urls
        },
        {
          headers: {
            'Content-Type': 'application/json',
          } 
        }
      ) 
      if (response.data?.code == 0) {
          return response.data.data
      } 
    } catch (error: any) {
      console.error('获取文件列表失败:', error)
      throw new Error(error.response?.data?.msg || '获取文件列表失败，请重试')
    }
  }
}

// 其他图纸相关接口
export const drawingApi = {
  /**
   * 上传其他图纸
   * @param productOrderId 订单ID
   * @param urls 图片URL列表
   */
  async upload(productOrderId: number, urls: string[]) {
    try {
      console.log('保存图纸到订单:', { productOrderId, urls })
      
      // 串行上传每张图片
      for (const url of urls) {
        const result = await api.post('/productOrder/updateDraw', {
          productOrderId,
          type: 0,
          img: url,
          operate: 0 // 新增操作
        })
        
        if (result.code !== 0) {
          console.error('上传图片失败:', result)
          return result
        }
      }
      
      // 所有图片上传成功
      return { code: 0, msg: '上传成功' }
    } catch (error: any) {
      console.error('上传图纸失败:', error)
      throw new Error(error.response?.data?.msg || '上传图纸失败，请重试')
    }
  },

  /**
   * 替换其他图纸
   * @param params 替换参数
   */
  async replace(params: {
    productOrderId: number
    type: number
    img: string
    target: string
  }) {
    try {
      console.log('替换图纸, 参数:', params)
      const result = await api.post('/productOrder/updateDraw', {
        ...params,
        operate: 1 // 替换操作
      })
      console.log('替换图纸结果:', result)
      return result
    } catch (error: any) {
      console.error('替换图纸失败:', error)
      throw new Error(error.response?.data?.msg || '替换图纸失败，请重试')
    }
  },

  /**
   * 删除其他图纸
   * @param params 删除参数
   */
  async delete(params: {
    productOrderId: number
    type: number
    img: string
  }) {
    try {
      const response = await api.post('/productOrder/updateDraw', {
        ...params,
        operate: 2 // 删除操作
      })
      return response
    } catch (error: any) {
      console.error('删除图纸失败:', error)
      throw new Error(error.response?.data?.msg || '删除图纸失败，请重试')
    }
  },

  /**
   * 获取其他图纸列表
   * @param productOrderId 订单ID
   */
  async getList(productOrderId: number) {
    try {
      console.log('开始获取图纸列表, 订单ID:', productOrderId)
      const response = await api.get(`/productOrder/orderDetail`, {
        params: {
          productOrderId,
          module: 0
        }
      })
      
      console.log('获取图纸列表响应:', response)
      
      if (!response.data) {
        throw new Error('未获取到数据')
      }
      
      // 解析图纸数据
      let drawings: string[] = []
      try {
        if (response.data.drawsHeader) {
          const headerDraws = JSON.parse(response.data.drawsHeader)
          if (Array.isArray(headerDraws)) {
            drawings = headerDraws
          } else {
            console.warn('drawsHeader 不是数组:', headerDraws)
          }
        }
      } catch (parseError) {
        console.error('解析图纸数据失败:', parseError)
        throw new Error('解析图纸数据失败')
      }
      
      console.log('解析后的图纸列表:', drawings)
      return {
        code: 0,
        data: drawings,
        msg: 'success'
      }
    } catch (error: any) {
      console.error('获取图纸列表失败:', error)
      throw new Error(error.response?.data?.msg || '获取图纸列表失败')
    }
  },

  /**
   * 获取其他图纸操作日志
   * @param params 查询参数
   */
  async getDrawLog(params: {
    type: number
    productOrderId: number
  }) {
    return api.post('/productOrder/getDrawLog', params)
  },
}

// 产品图纸相关接口
export const productDrawingApi = {
  /**
   * 获取产品图纸列表
   * @param productId 产品ID
   */
  async getDrawList(productId: number) {
    return api.get(`/product/draws/${productId}`)
  },

  /**
   * 上传产品图纸
   * @param params 上传参数
   */
  async uploadDraw(params: {
    productOrderItemId: number
    type: number
    img: string
  }) {
    return api.post('/productOrder/updateDraw', params)
  },

  /**
   * 批量上传产品图纸
   * @param productOrderItemId 产品ID
   * @param type 类型
   * @param urls 图片URL列表
   */
  async uploadDraws(productOrderItemId: number, type: number, urls: { img: string, name: string }[]): Promise<ApiResponse> {
    try {
      console.log('保存图纸到产品:', { productOrderItemId, type, urls })
      
      for (const url of urls) {
        console.log('上传图片:', url)
        const result: ApiResponse = await api.post('/productOrder/updateDraw', {
          productOrderItemId,
          type,
          img: url.img,
          name: url.name
        })
        
        if (result.code !== 0) {
          console.error('上传图片失败:', result)
          return result
        }
      }
      
      return { code: 0, msg: '上传成功', data: null }
    } catch (error: any) {
      console.error('上传图纸失败:', error)
      throw new Error(error.response?.data?.msg || '上传图纸失败，请重试')
    }
  },

  /**
   * 替换产品图纸
   * @param params 替换参数
   */
  async replaceDraw(params: {
    productOrderItemId: number
    type: number
    img: string
    target: string
  }) {
    return api.post('/productOrder/updateDraw', {
      ...params,
      operate: 1
    })
  },

  /**
   * 删除产品图纸
   * @param params 删除参数
   */
  async deleteDraw(params: {
    productOrderItemId: number
    type: number
    img: string
  }) {
    return api.post('/productOrder/updateDraw', {
      ...params,
      operate: 2
    })
  },

  /**
   * 获取产品图纸操作记录
   * @param params 查询参数
   */
  async getDrawLog(params: {
    type: number
    productOrderItemId: number
  }) {
    return api.post('/productOrder/getDrawLog', params)
  },

  // 提交审核
  submitReview(params: {
    id: number
    submitReviewStatus: number
  }): Promise<ApiResponse> {
    console.log('提交审核参数:', params)
    return api.post('/productOrder/submitReview', params)
  },

  // 获取审核状态
  getReviewStatus(productId: number): Promise<ApiResponse> {
    return api.get(`/productOrder/reviewStatus/${productId}`)
  }
}

// 财务审核相关接口类型定义
export interface FinanceReviewItem {
  id: number
  productOrderId: number
  no: string
  batch: string
  name: string
  count: string
  so: string
  customerName: string
  project: string
  submitReviewTime: number
  submitReviewTimeText: string
  financeReviewStatus: number
  financeReviewStatusText: string
  groupReviewStatus: number    // 集团审核状态：0待审核 1审核通过 2审核拒绝
  groupReviewStatusText: string
  order?: {
    id: number
    so: string
    customerName: string
    project: string
  }
}

// 财务审核状态映射
export const FINANCE_REVIEW_STATUS = {
  PENDING: 0,    // 待审核
  APPROVED: 1,   // 审核通过
  REJECTED: 2    // 审核拒绝
}

// 财务审核状态文字映射
export const FINANCE_REVIEW_STATUS_TEXT = {
  [FINANCE_REVIEW_STATUS.PENDING]: '待审核',
  [FINANCE_REVIEW_STATUS.APPROVED]: '审核通过',
  [FINANCE_REVIEW_STATUS.REJECTED]: '审核拒绝'
}

export interface FinanceReviewQuery {
  page: number
  pageSize: number
  financeReviewStatus: number
  submitReviewTimeStart?: string
  submitReviewTimeEnd?: string
}

export interface FinanceReviewSubmit {
  id: number
  financeReviewStatus: number
  comment?: string
}

// 财务审核相关接口
export const financeApi = {
  // 获取财务审核列表
  async getFinanceReviewList(params: FinanceReviewQuery): Promise<ApiResponse<{
    total: number
    list: FinanceReviewItem[]
  }>> {
    try {
      console.log('获取财务审核列表, 参数:', params)
      const requestData = {
        page: params.page,
        pageSize: params.pageSize,
        financeReviewStatus: params.financeReviewStatus,
        submitReviewTimeStart: params.submitReviewTimeStart || '',
        submitReviewTimeEnd: params.submitReviewTimeEnd || ''
      }
      console.log('发送请求数据:', requestData)
      const response = await api.post('/productOrder/financeReviewList', requestData)
      console.log('财务审核列表响应:', response)
      
      // 处理响应数据
      if (response.code === 0) {
        // 如果响应数据是数组，直接使用
        if (Array.isArray(response.data)) {
          return processFinanceReviewList(response.data);
        } 
        // 如果响应数据是单个对象，将其转换为数组
        else if (response.data && typeof response.data === 'object') {
          console.log('响应数据是单个对象，转换为数组');
          return processFinanceReviewList([response.data]);
        }
      }
      
      return {
        code: response.code || 0,
        msg: response.message || 'success',
        data: {
          total: 0,
          list: []
        }
      }
    } catch (error: any) {
      console.error('获取财务审核列表失败:', error)
      throw new Error(error.message || '获取财务审核列表失败，请重试')
    }
  },

  // 提交财务审核
  async submitFinanceReview(data: FinanceReviewSubmit): Promise<ApiResponse<void>> {
    try {
      console.log('提交财务审核, 参数:', data)
      const requestData = {
        id: data.id,
        financeReviewStatus: data.financeReviewStatus
      }
      console.log('发送请求数据:', requestData)
      const response = await api.post('/productOrder/financeReview', requestData)
      console.log('提交财务审核响应:', response)
      return response
    } catch (error: any) {
      console.error('提交财务审核失败:', error)
      throw new Error(error.message || '提交财务审核失败，请重试')
    }
  }
}

// 处理财务审核列表数据
function processFinanceReviewList(data: any[]): ApiResponse<{
  total: number
  list: FinanceReviewItem[]
}> {
  return {
    code: 0,
    msg: 'success',
    data: {
      total: data.length,
      list: data.map((item: any) => {
        // 格式化时间
        const submitTime = new Date(item.submitReviewTime)
        const timeText = submitTime.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(/\//g, '-')

        return {
          id: item.id,
          productOrderId: item.productOrderId,
          no: item.no,
          batch: item.batch,
          name: item.name,
          count: item.count,
          so: item.order?.so || '',
          customerName: item.order?.customerName || '',
          project: item.order?.project || '',
          order: item.order,
          submitReviewTime: item.submitReviewTime,
          submitReviewTimeText: timeText,
          financeReviewStatus: item.financeReviewStatus,
          financeReviewStatusText: FINANCE_REVIEW_STATUS_TEXT[item.financeReviewStatus] || '未知状态',
          groupReviewStatus: item.groupReviewStatus,
          groupReviewStatusText: GROUP_REVIEW_STATUS_TEXT[item.groupReviewStatus] || '未知状态'
        }
      })
    }
  }
}

// 集团审核状态映射
export const GROUP_REVIEW_STATUS = {
  PENDING: 0,    // 待审核
  APPROVED: 1,   // 审核通过
  REJECTED: 2    // 审核拒绝
}

// 集团审核状态文字映射
export const GROUP_REVIEW_STATUS_TEXT = {
  [GROUP_REVIEW_STATUS.PENDING]: '待审核',
  [GROUP_REVIEW_STATUS.APPROVED]: '审核通过',
  [GROUP_REVIEW_STATUS.REJECTED]: '审核拒绝'
}

// 集团审核接口参数类型
export interface GroupReviewQuery {
  page: number
  pageSize: number
  groupReviewStatus?: number
  submitReviewTimeStart?: string
  submitReviewTimeEnd?: string
}

export interface GroupReviewItem {
  id: number
  productOrderId: number
  no: string
  batch: string
  name: string
  count: string
  so: string
  customerName: string
  project: string
  submitReviewTime: number
  submitReviewTimeText: string
  financeReviewStatus: number  // 财务审核状态：0待审核 1审核通过 2审核拒绝
  financeReviewStatusText: string
  groupReviewStatus: number
  groupReviewStatusText: string
  order?: {
    id: number
    so: string
    customerName: string
    project: string
  }
}

export interface GroupReviewSubmit {
  id: number
  groupReviewStatus: number
}

// 集团审核 API
export const groupApi = {
  // 获取集团审核列表
  async getGroupReviewList(params: GroupReviewQuery): Promise<ApiResponse<{
    total: number
    list: GroupReviewItem[]
  }>> {
    try {
      console.log('获取集团审核列表, 参数:', params)
      const requestData = {
        page: params.page,
        pageSize: params.pageSize,
        groupReviewStatus: params.groupReviewStatus,
        submitReviewTimeStart: params.submitReviewTimeStart || '',
        submitReviewTimeEnd: params.submitReviewTimeEnd || ''
      }
      console.log('发送请求数据:', requestData)
      const response = await api.post('/productOrder/groupReviewList', requestData)
      console.log('集团审核列表响应:', response)
      
      // 处理响应数据
      if (response.code === 0) {
        // 如果响应数据是数组，直接使用
        if (Array.isArray(response.data)) {
          return processGroupReviewList(response.data);
        } 
        // 如果响应数据是单个对象，将其转换为数组
        else if (response.data && typeof response.data === 'object') {
          console.log('响应数据是单个对象，转换为数组');
          return processGroupReviewList([response.data]);
        }
      }
      
      return {
        code: response.code || 0,
        msg: response.message || 'success',
        data: {
          total: 0,
          list: []
        }
      }
    } catch (error: any) {
      console.error('获取集团审核列表失败:', error)
      throw new Error(error.message || '获取集团审核列表失败，请重试')
    }
  },

  // 提交集团审核
  submitGroupReview: async (data: GroupReviewSubmit): Promise<ApiResponse<void>> => {
    try {
      console.log('提交集团审核, 参数:', data)
      const requestData = {
        id: data.id,
        groupReviewStatus: data.groupReviewStatus
      }
      console.log('发送请求数据:', requestData)
      const response = await api.post('/productOrder/groupReview', requestData)
      console.log('提交集团审核响应:', response)
      return response
    } catch (error: any) {
      console.error('提交集团审核失败:', error)
      throw new Error(error.message || '提交集团审核失败，请重试')
    }
  }
}

// 处理集团审核列表数据
function processGroupReviewList(data: any[]): ApiResponse<{
  total: number
  list: GroupReviewItem[]
}> {
  return {
    code: 0,
    msg: 'success',
    data: {
      total: data.length,
      list: data.map((item: any) => {
        // 格式化时间
        const submitTime = new Date(item.submitReviewTime)
        const timeText = submitTime.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(/\//g, '-')

        return {
          id: item.id,
          productOrderId: item.productOrderId,
          no: item.no,
          batch: item.batch,
          name: item.name,
          count: item.count,
          so: item.order?.so || '',
          customerName: item.order?.customerName || '',
          project: item.order?.project || '',
          order: item.order,
          submitReviewTime: item.submitReviewTime,
          submitReviewTimeText: timeText,
          financeReviewStatus: item.financeReviewStatus,
          financeReviewStatusText: FINANCE_REVIEW_STATUS_TEXT[item.financeReviewStatus] || '未知状态',
          groupReviewStatus: item.groupReviewStatus,
          groupReviewStatusText: GROUP_REVIEW_STATUS_TEXT[item.groupReviewStatus] || '未知状态'
        }
      })
    }
  }
}

// 导出默认配置
export const apiConfig = {
  baseURL: api.defaults.baseURL,
  timeout: api.defaults.timeout,
  headers: api.defaults.headers
}

// 类型定义
export interface UserInfo {
  id: number
  mobilePhone: string
  password: string
  username: string
  nickname: string
  extra: string
  dept: string
  job: string
  showNickname: string
}

export interface LoginResponse {
  user: UserInfo
  token: string
  register: boolean
}

export interface OrderItem {
  id: number
  no: string
  name: string
  batch: string
  count: string
  cover: string
  width: number
  depth: number
  height: number
  delivery: string
}

export interface OrderData {
  id: number
  so: string
  customerName: string
  project: string
  day: number
  itemList: OrderItem[]
}

export interface UpdateDrawParams {
  id: string | number
  operate: number
  img: string
  target?: string
}

export const group_REVIEW_STATUS = {
  PENDING: 0,    // 待审核
  APPROVED: 1,   // 已通过
  REJECTED: 2,   // 已拒绝
}

// 工厂相关接口类型定义
export interface FactoryItem {
  id: number
  name: string
}

export interface FactoryAssignItemResponse {
  id: number
  productOrderId: number
  no: string
  batch: string
  name: string
  count: string
  draws: string
  drawsErrorStatus: number
  financeReviewStatus: number
  financeReviewTime: number
  groupReviewStatus: number
  groupReviewTime: number
  submitReviewStatus: number
  submitReviewTime: number
  order?: {
    id: number
    so: string
    customer: string
    customerName: string
    customerNo: string
  }
}

export interface FactoryAssignItem {
  id: number
  productOrderId: number
  no: string
  batch: string
  name: string
  count: string
  so: string
  customerName: string
  project: string
  submitReviewTime: number
  submitReviewTimeText: string
  financeReviewStatus: number
  financeReviewStatusText: string
  groupReviewStatus: number
  groupReviewStatusText: string
  assignFactoryStatus?: number
  assignFactoryStatusText?: string
  processList?: Array<{
    id?: number
    name: string
  }>
}

// 工厂分配状态映射
export const FACTORY_ASSIGN_STATUS = {
  PENDING: 0,    // 待分配
  ASSIGNED: 1    // 已分配
}

// 工厂分配状态文字映射
export const FACTORY_ASSIGN_STATUS_TEXT = {
  [FACTORY_ASSIGN_STATUS.PENDING]: '待分配',
  [FACTORY_ASSIGN_STATUS.ASSIGNED]: '已分配'
}

// 工厂分配接口参数类型
export interface FactoryAssignQuery {
  page: number
  pageSize: number
  assignFactoryStatus?: number
  submitReviewTimeStart?: string
  submitReviewTimeEnd?: string
}

// 工厂分配 API
export const factoryApi = {
  // 获取工厂分配列表
  async getAssignList(params: FactoryAssignQuery): Promise<ApiResponse<{
    total: number
    list: FactoryAssignItem[]
  }>> {
    try {
      console.log('获取工厂分配列表, 参数:', params)
      
      // 构建请求数据
      const requestData = {
        page: params.page,
        pageSize: params.pageSize,
        assignFactoryStatus: params.assignFactoryStatus,
        submitReviewTimeStart: params.submitReviewTimeStart || '',
        submitReviewTimeEnd: params.submitReviewTimeEnd || ''
      }
      
      console.log('发送数据:', requestData)
      
      // 更新为正确的接口路径
      const response = await api.post('/productOrder/assignFactoryList', requestData)
      
      console.log('工厂分配列表响应:', response)
      console.log('响应数据类型:', typeof response)
      
      // 处理响应数据
      if (response && response.code === 0) {
        // 如果响应数据是数组，直接使用
        if (Array.isArray(response.data)) {
          console.log('响应数据是数组，长度:', response.data.length)
          return processFactoryAssignList(response.data);
        }
        
        // 如果响应数据是单个对象，转换为数组
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          console.log('响应数据是单个对象，转换为数组')
          return processFactoryAssignList([response.data]);
        }
      }
      
      return {
        code: response?.code || 0,
        msg: response?.message || response?.msg || 'success',
        data: {
          total: 0,
          list: []
        }
      }
    } catch (error: any) {
      console.error('获取工厂分配列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取工厂分配列表失败',
        data: {
          total: 0,
          list: []
        }
      }
    }
  },
  
  // 分配工厂
  assignFactory: async (params: { id: number; typeOf: string }) => {
    try {
      console.log('分配工厂, 参数:', params)
      const response = await api.post('/productOrder/assignFactory', params)
      console.log('分配工厂响应:', response)
      return {
        code: response.code || 0,
        msg: response.message || '操作成功',
        data: null
      }
    } catch (error: any) {
      console.error('分配工厂失败:', error)
      throw new Error(error.message || '分配工厂失败，请重试')
    }
  }
}

// 处理工厂分配列表数据
function processFactoryAssignList(data: any[]): ApiResponse<{
  total: number
  list: FactoryAssignItem[]
}> {
  console.log('处理工厂分配列表数据，原始数据:', data);
  
  try {
    const processedList = data.map((item: any) => {
      // 格式化时间
      let timeText = '';
      try {
        if (item.submitReviewTime) {
          const submitTime = new Date(item.submitReviewTime);
          timeText = submitTime.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).replace(/\//g, '-');
        }
      } catch (error) {
        console.error('时间格式化错误:', error);
        timeText = '';
      }

      // 确定工厂分配状态
      const assignFactoryStatus = item.assignFactoryStatus !== undefined ? item.assignFactoryStatus : 0;
      const assignFactoryStatusText = FACTORY_ASSIGN_STATUS_TEXT[assignFactoryStatus] || '未知状态';

      // 处理工序列表
      let processList = [];
      try {
        if (item.processList && Array.isArray(item.processList)) {
          processList = item.processList.map((process: any) => ({
            id: process.id || undefined,
            name: process.name || ''
          }));
        } else if (typeof item.processList === 'string') {
          // 如果是字符串，尝试解析 JSON
          try {
            const parsed = JSON.parse(item.processList);
            if (Array.isArray(parsed)) {
              processList = parsed.map(process => ({
                id: process.id || undefined,
                name: process.name || ''
              }));
            }
          } catch (e) {
            console.warn('解析工序列表失败:', e);
          }
        }
      } catch (error) {
        console.error('处理工序列表失败:', error);
      }

      return {
        id: item.id,
        productOrderId: item.productOrderId,
        no: item.no || '',
        batch: item.batch || '',
        name: item.name || '',
        count: item.count || '',
        so: item.order?.so || '',
        customerName: item.order?.customerName || '',
        project: item.order?.project || '',
        submitReviewTime: item.submitReviewTime || 0,
        submitReviewTimeText: timeText,
        financeReviewStatus: item.financeReviewStatus || 0,
        financeReviewStatusText: FINANCE_REVIEW_STATUS_TEXT[item.financeReviewStatus] || '未知状态',
        groupReviewStatus: item.groupReviewStatus || 0,
        groupReviewStatusText: GROUP_REVIEW_STATUS_TEXT[item.groupReviewStatus] || '未知状态',
        assignFactoryStatus: assignFactoryStatus,
        assignFactoryStatusText: assignFactoryStatusText,
        typeOf: item.typeOf || '',
        processList: processList  // 添加工序列表
      };
    });

    console.log('处理后的工厂分配列表数据:', processedList);
    
    return {
      code: 0,
      msg: 'success',
      data: {
        total: data.length,
        list: processedList
      }
    };
  } catch (error) {
    console.error('处理工厂分配列表数据出错:', error);
    return {
      code: 0,
      msg: 'success',
      data: {
        total: 0,
        list: []
      }
    };
  }
}

// 用户管理相关接口类型定义
export interface JFUserListQuery {
  page: number
  pageSize: number
  auth?: string
  mobilePhone?: string
  showNickname?: string
}

export interface JFUserListResponse {
  records: JFUserItem[]
  total: number
  pages: number 
  size: number
  current: number
  
}

export interface JFUserItem {
  id: number
  mobilePhone: string
  nickname: string
  auths: string
  v: number
  fast: number
  authTrial: number
  createTime: number
  fastTimes: number
  beta: number
  extra: string
  dept: string
  job: string
  showNickname: string
  email?: string
  origin_job?: string  // 添加简称字段
}

// 用户管理API
export const jfUserApi = {
  // 获取用户列表
  async getList(params: JFUserListQuery): Promise<ApiResponse<JFUserListResponse>> {
    const response = await api.post('/manage/jfUser/list', params)
    return response
  },

  // 新增/更新用户
  async update(params: {
    extra: string
    auths: string
    nickname: string
    mobilePhone: string
    email: string
  }): Promise<ApiResponse<void>> {
    const response = await api.post('/manage/jfUser/update', params)
    return response
  }
}

// 货品管理相关接口类型定义
export interface CategoryItem {
  id: number
  categoryNo: string      // 分类编号
  categoryName: string    // 分类名称
  remark: string | null   // 分类备注
  parentCategoryNo: string | null  // 父分类编号
  children: CategoryItem[] // 子分类
}

export interface WarehouseItem {
  warehouseNo: string     // 仓库编号
  warehouseName: string   // 仓库名称
}

export interface GoodsListQuery {
  goodsNoOrName?: string  // 货品编码或名称
  warehouseNo?: string    // 仓库编码
  inventoryNo?: string    // 库存编码
  goodsAttr?: string     // 属性
  startDate?: string     // 开始日期
  endDate?: string       // 结束日期
  page: number
  pageSize: number
}

export interface GoodsListResponse {
  records: GoodsItem[]
  total: number
  size: number
  current: number
  pages: number
}

export interface GoodsItem {
  goodsNo: string        // 货品编码
  goodsName: string      // 货品名称
  goodsSpec: string      // 规格
  goodsDesc: string      // 描述
  goodsAttr: string      // 属性
  inventoryNo: string    // 库存编码
  inventoryName: string  // 库存名称
  inventoryQuantity: number  // 库存数量
  purchaseQuantity: number   // 采购中数量
  pendingQuantity: number    // 待领用数量
  openingBalance: number     // 期初余额
  closingBalance: number     // 期末余额
  taxCode: string        // 税码
  unit: string          // 单位
  warehouseNo: string   // 仓库编码
  warehouseName: string // 仓库名称
  locationNo: string    // 货位编码
  locationName: string  // 货位名称
  featureCode: string   // 特征码
  singleWeight: number  // 单重
  singleWeightUnit: string // 单重单位
}

// 货品管理相关接口
export const goodsManageApi = {
  // 获取分类列表
  async getCategoryList(): Promise<ApiResponse<CategoryItem[]>> {
    try {
      console.log('获取分类列表')
      const response = await api.get('/jf/goodsManage/getCategoryList')
      console.log('分类列表响应:', response)
      return {
        code: 0,
        msg: 'success',
        data: response.data || []
      }
    } catch (error: any) {
      console.error('获取分类列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取分类列表失败，请重试',
        data: []
      }
    }
  },

  // 获取仓库列表
  async getWarehouseList(): Promise<ApiResponse<WarehouseItem[]>> {
    try {
      console.log('获取仓库列表')
      const response = await api.get('/jf/goodsManage/getWarehouseList')
      console.log('仓库列表响应:', response)
      return {
        code: 0,
        msg: 'success',
        data: response.data || []
      }
    } catch (error: any) {
      console.error('获取仓库列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取仓库列表失败，请重试',
        data: []
      }
    }
  },

  // 获取货品列表
  async getGoodsList(params: GoodsListQuery): Promise<ApiResponse<GoodsListResponse>> {
    try {
      console.log('获取货品列表, 参数:', params)
      const response = await api.post('/jf/goodsManage/getGoodsList', params)
      console.log('货品列表原始响应:', response)
      
      // 处理响应数据
      const responseData = response.data || {}
      return {
        code: 0,
        msg: 'success',
        data: {
          records: responseData.records || [],
          total: responseData.total || 0,
          size: responseData.size || 10,
          current: responseData.current || 1,
          pages: responseData.pages || 0
        }
      }
    } catch (error: any) {
      console.error('获取货品列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取货品列表失败，请重试',
        data: {
          records: [],
          total: 0,
          size: 10,
          current: 1,
          pages: 0
        }
      }
    }
  }
}

// 库存相关接口类型定义
export interface InventoryListQuery {
  inventoryNo?: string   // 库存编码
  inventoryName?: string // 库存名称
  warehouseNo?: string   // 仓库编码
  page: number
  pageSize: number
}

export interface InventoryItem {
  inventoryNo: string    // 库存编码
  inventoryName: string  // 库存名称
  warehouseNo: string    // 仓库编码
  warehouseName: string  // 仓库名称
  locationNo: string     // 货位编码
  locationName: string   // 货位名称
  quantity: number       // 库存数量
  unit: string          // 单位
  createTime: string    // 创建时间
  updateTime: string    // 更新时间
}

export interface InventoryListResponse {
  total: number
  list: InventoryItem[]
}

// 合并货品列表查询参数
export interface MergedGoodsListQuery {
  page: number             // 页码
  pageSize: number         // 每页记录数
  keyword?: string         // 可选，支持货品编号、名称模糊搜索
  goodsAttr?: string       // 可选，货品属性模糊匹配
  warehouseNo?: string     // 可选，仓库编号精确匹配
  inventoryNo?: string     // 可选，库存编码模糊匹配
  inventoryName?: string   // 可选，库存名称模糊匹配
  beginDate?: string       // 可选，开始日期，格式：yyyy-MM-dd
  endDate?: string         // 可选，结束日期，格式：yyyy-MM-dd
}

// 仓库库存明细
export interface WarehouseInventory {
  warehouseNo: string      // 仓库编号
  warehouseName: string    // 仓库名称
  initialQuantity: number  // 该仓库期初数量
  currentInQuantity: number // 该仓库本期进项
  currentOutQuantity: number // 该仓库本期出项
  endQuantity: number      // 该仓库期末数量
  inventoryQuantity: number // 该仓库当前库存数量
}

// 合并货品列表项
export interface MergedGoodsItem {
  goodsNo: string          // 货品编码
  goodsName: string        // 货品名称
  goodsSpec: string        // 货品规格
  goodsDesc: string        // 货品描述
  goodsAttr: string        // 货品属性
  inventoryNo: string      // 库存编码
  inventoryName: string    // 库存名称
  category?: string        // 货品分类，可能的属性字段
  property?: string        // 货品属性，可能的属性字段
  taxCode: string          // 税码
  unit: string             // 单位
  warehouseNo: string      // 仓库编码
  warehouseName: string    // 仓库名称
  locationNo: string       // 货位编码
  locationName: string     // 货位名称
  totalInitialQuantity: number    // 总期初数量
  totalCurrentInQuantity: number  // 总本期进项
  totalCurrentOutQuantity: number // 总本期出项
  totalEndQuantity: number        // 总期末数量
  totalInventoryQuantity: number  // 总库存数量（所有仓库）
  featureCode: string      // 特征编码
  singleWeight: number     // 单重
  singleWeightUnit: string // 单重单位
  organization: string     // 组织
  purchaseQuantity: number // 采购数量
  pendingQuantity: number  // 待领用数量
  purchasingQuantity: number // 采购中数量
  pendingPickQuantity: number // 待领用数量
  warehouseInventories: WarehouseInventory[] // 各仓库库存明细
}

// 合并货品列表响应
export interface MergedGoodsListResponse {
  records: MergedGoodsItem[]
  total: number            // 总记录数
  pages: number            // 总页数
  current: number          // 当前页
  size: number             // 每页记录数
}

// 库存管理相关接口
export const inventoryApi = {
  // 获取库存列表
  async getInventoryList(params: InventoryListQuery): Promise<ApiResponse<InventoryListResponse>> {
    try {
      console.log('获取库存列表, 参数:', params)
      const response = await api.post('/jf/goodsManage/getInventoryList', params)
      
      // 处理响应数据
      return {
        code: response.data.code || 0,
        msg: response.data.msg || 'success',
        data: {
          total: response.data.data?.length || 0,
          list: response.data.data || []
        }
      }
    } catch (error: any) {
      console.error('获取库存列表失败:', error)
      throw new Error(error.message || '获取库存列表失败，请重试')
    }
  },
  
  // 获取合并后的货品列表
  async getMergedGoodsList(params: MergedGoodsListQuery): Promise<ApiResponse<MergedGoodsListResponse>> {
    try {
      console.log('获取合并货品列表, 参数:', params)
      const response = await api.post('/jf/goods/getMergedGoodsList', params)
      console.log('合并货品列表响应:', response)
      
      // 处理响应数据
      return {
        code: 0,
        msg: 'success',
        data: response.data || {
          records: [],
          total: 0,
          pages: 0,
          current: 1,
          size: 10
        }
      }
    } catch (error: any) {
      console.error('获取合并货品列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取合并货品列表失败，请重试',
        data: {
          records: [],
          total: 0,
          pages: 0,
          current: 1,
          size: 10
        }
      }
    }
  }
}

// 采购单列表查询参数接口
export interface PurchaseListQuery {
  page: number
  pageSize: number
  keyword?: string
}

// 采购单列表项接口
export interface PurchaseListItem {
  id: number
  purchaseOrderNo: string    // 采购单号
  supplierName: string      // 供应商名称
  supplierNo: string        // 供应商编号
  so?: string              // SO号
  billDate: number         // 单据日期
  schDate: number          // 计划交期
  createTime: number       // 创建时间
}

// 采购单列表响应接口
export interface PurchaseListResponse {
  records: PurchaseListItem[]
  total: number
  size: number
  current: number
  pages: number
}

// 采购单详情项接口
export interface PurchaseDetailItem {
  id: number
  purchaseOrderId: number
  serialNo?: number
  goodsNo: string
  goodsName: string
  goodsSpec: string
  goodsDesc?: string
  goodsColor?: string  // 颜色
  custom?: string      // 销售产品编号  
  quantity: number
  unit: string
  guardStatus: number
  qualityStatus: number
  warehouseStatus: number
  createTime?: number
  updateTime?: number
}

// 采购单订单信息接口
export interface PurchaseOrder {
  id: number
  purchaseOrderNo: string
  supplierName: string
  supplierNo: string
  so: string
  billDate?: number
  schDate?: number
  createTime?: number
  status?: number
}

// 付款申请记录接口
export interface PaymentApplicationItem {
  id: number
  paymentOrderNo: string    // 发起申请
  paymentAmount: number     // 申请金额
  paymentPercentage?: number // 百分比
  signerName?: string       // 签字人
  receiptStatus?: string    // 回单状态
  createTime?: number       // 创建时间
  status?: number           // 状态
}

// 采购单详情响应接口
export interface PurchaseDetailResponse {
  order: PurchaseOrder
  itemList: PurchaseDetailItem[]
  paymentApplicationList: PaymentApplicationItem[]  // 付款申请列表
}

// 采购相关接口
export const purchaseApi = {
  // 获取采购单列表
  async getPurchaseList(params: PurchaseListQuery): Promise<ApiResponse<PurchaseListResponse>> {
    try {
      console.log('获取采购单列表, 参数:', params)
      const response: any = await api.post('/jf/purchase/list', params)
      console.log('采购单列表响应:', response)
      
      return {
        code: response.code || 0,
        msg: response.msg || 'success',
        data: response.data || {
          records: [],
          total: 0,
          size: params.pageSize,
          current: params.page,
          pages: 0
        }
      }
    } catch (error: any) {
      console.error('获取采购单列表失败:', error)
      return {
        code: -1,
        msg: error.message || '获取采购单列表失败，请重试',
        data: {
          records: [],
          total: 0,
          size: params.pageSize,
          current: params.page,
          pages: 0
        }
      }
    }
  },

  // 获取采购单详情
  async getPurchaseDetail(id: number): Promise<ApiResponse<PurchaseDetailResponse>> {
    try {
      console.log('获取采购单详情, ID:', id)
      const response: any = await api.get(`/jf/purchase/detail?id=${id}`)
      console.log('采购单详情响应:', response)
      
      return {
        code: response.code || 0,
        msg: response.msg || 'success',
        data: response.data || {
          order: {},
          itemList: []
        }
      }
    } catch (error: any) {
      console.error('获取采购单详情失败:', error)
      throw new Error(error.message || '获取采购单详情失败，请重试')
    }
  },
  // 获取供应商列表
  async vendorList(params: any) {
    return api.post('/jf/vendor/list', params)
  },
  // 新增供应商
  async vendorAdd(params: any) {
    return api.post('/jf/vendor/add', params)
  },
  // 编辑供应商
  async vendorUpdate(params: any) {
    return api.post('/jf/vendor/update', params)
  },
  // 删除供应商
  async vendorDelete(params: any) {
    return api.post('/jf/vendor/delete', params)
  },
  // 料单汇总
  async liaodanSummary(params: any) {
    return api.post('/jf/production-bom/contract/summary', params)
  },
  // 估价汇总
  // 采购汇总
  async purchaseSummary(params: any) {
    return api.post('/jf/purchase/summary/list', params)
  },
  // 料单汇总查看详情
  async bomSummaryDetail(params: any) {
    return api.post('/jf/production-bom/contract/summary/detail', params)
  },
  // 获取料单导入模版地址
  async getImportTemplate(contractId: number) {
    return api.get(`/jf/production-bom/import/template?contractId=${contractId}`)
  },
  // 设置料单导入模版地址
  async setImportTemplate(params: any) {
    return api.post('/jf/production-bom/import/template/set', params)
  },
  // 发起采购
  async purchaseInitiate(params: any) {
    return api.post('/jf/purchase/initiate', params)
  },
  // 发起付款申请
  async purchasePaymentApply(params: any) {
    return api.post('/jf/purchase/payment/apply', params)
  },
  // 提交付款申请
  async purchasePaymentSubmit(params: any) {
    return api.post('/jf/purchase/payment/submit', params)
  },
  // 删除付款申请
  async purchasePaymentDelete(params: any) {
    return api.post('/jf/purchase/payment/delete', params)
  },
  // 付款申请签字
  async purchasePaymentSign(params: any) {
    return api.post('/jf/purchase/payment/sign', params)
  },
  // 上传回单
  async purchasePaymentUploadReceipt(params: any) {
    return api.post('/jf/purchase/payment/uploadReceipt', params)
  },
  // 全部采购单列表
  async purchaseListAll(params: any) {
    return api.post('/jf/purchase/list/sign', params)
  },
  // 我的采购单列表
  async purchaseListMy(params: any) {
    return api.post('/jf/purchase/list/my', params)
  },
  // 采购单汇总
  async purchaseSummaryList(params: any) {
    return api.post('/jf/purchase/list', params)
  },
  // 财务采购单列表
  async purchaseListFinance(params: any) {
    return api.post('/jf/purchase/list/finance', params)
  },
  // 合同下的采购单
  async purchaseListContract(params: any) {
    return api.post('/jf/purchase/list/contract', params)
  },
  // 采购单详情
  async purchaseDetail(id: number) {
    return api.get(`/jf/purchase/detail?id=${id}`)
  },
  // 公告列表
  async announcementList(params: any) {
    return api.post('/jf/announcement/manage/list', params)
  },
  // 发布公告
  async publishAnnouncement(params: any) {
    return api.post('/jf/announcement/manage/publish', params)
  },
  // 部门列表
  async departmentsList() {
    return api.get('/jf/announcement/manage/departments')
  },
  // 部门人员列表
  async departmentsUsersList() {
    return api.get('/jf/announcement/manage/users')
  },
  // 公告二维码
  async noticeQrcode(id: number) {  
    return api.get(`/jf/announcement/manage/qrcode?id=${id}`)
  },
  // 新增请购单
  async purchaseRequisitionAdd(params: any) {
    return api.post('/jf/purchase/requisition/add', params)
  },
  // 请购单列表
  async purchaseRequisitionList(params: any) {
    return api.post('/jf/purchase/requisition/list', params)
  },
  // 我的请购单列表
  async purchaseRequisitionMyList(params: any) {
    return api.post('/jf/purchase/requisition/myList', params)
  },
  // 获取材料的库存 采购中 
  async purchaseProductQuantityInfo(params: any) {
    return api.post(`/jf/purchase/productQuantityInfo`, params)
  },
}

// 工序相关接口类型定义
export interface ProcessItem {
  id?: number        // 工序ID，修改时必填，新增时不填
  name: string       // 工序名称
}

export interface SaveProcessRequest {
  productOrderItemId: number  // 产品订单项ID
  processList: ProcessItem[]  // 工序列表
}

// 工序管理相关接口
export const processApi = {
  /**
   * 保存或更新工序
   * @param params 工序参数
   */
  async saveOrUpdate(params: SaveProcessRequest) {
    console.log('保存或更新工序, 参数:', params)
    return api.post('/jf/productOrderItemProcess/saveOrUpdate', params)
  }
}

// 导出工序相关方法
export const saveOrUpdateProcess = processApi.saveOrUpdate
